/**
 * CreateOrgWizard — §9.A 4-step org-onboarding wizard.
 *
 * Replaces the single-form `CreateOrgDialog` with a wizard that walks the
 * platform admin through:
 *
 *   Step 1 — Org details (name, slug auto-derived, jurisdiction)
 *   Step 2 — Subscription tier (free / starter / professional / enterprise)
 *            + storage allocation mode (shared / dedicated; S5 of the
 *              shared-storage plan — see ai-docs/lexiun-azure-storage.md)
 *   Step 3 — First ORG_ADMIN user (email, firstName, lastName)
 *   Step 4 — Confirm + provision; mounts <ProvisioningProgress> inline
 *            for the org-provision pipeline, then chains into the
 *            user-provision pipeline once the org is READY. Final state:
 *            "✓ Org + invitation sent" — post-2026-05-14 invitation-first
 *            flow (see ai-docs/lexiun-auth-guide.md); the new admin
 *            accepts the Clerk invitation email and sets a password
 *            rather than the pre-2026-05-14 authless-user flow that
 *            required out-of-band OTP delivery.
 *
 * Uses Radix `Dialog` (the Shadcn wrapper at `@/components/ui/dialog`).
 * Stays self-contained — no Zod, no per-step files yet; keeps the
 * implementation in one place until product feedback says we need to
 * split it.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  Globe,
  Info,
  KeyRound,
  Loader2,
  Mail,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import { ProvisioningProgress } from "./ProvisioningProgress";
import { errorText } from "@/lib/task-error";
import type { TaskRecord } from "@/types/platform";

// ── Types ────────────────────────────────────────────────────────

type Tier = "free" | "starter" | "professional" | "enterprise";

interface TierOption {
  value: Tier;
  label: string;
  description: string;
  perks: readonly string[];
}

const TIERS: readonly TierOption[] = [
  {
    value: "free",
    label: "Free",
    description: "Single user, evaluation only",
    perks: ["1 user", "Local-only data", "No production SLA"],
  },
  {
    value: "starter",
    label: "Starter",
    description: "Small firm — up to 10 users",
    perks: ["10 users", "Standard agent runtime", "Email support"],
  },
  {
    value: "professional",
    label: "Professional",
    description: "Mid-sized firm — up to 50 users",
    perks: ["50 users", "Enhanced agent runtime", "Priority support"],
  },
  {
    value: "enterprise",
    label: "Enterprise",
    description: "Large firm — unlimited users",
    perks: ["Unlimited users", "Dedicated runtime", "SLA + dedicated CSM"],
  },
];

// ── Storage mode (S5 of the shared-storage plan) ───────────────
// See ai-docs/lexiun-azure-storage.md for the full design + threat model.
// CREATE-only — immutable post-provision (UpdateOrgSchema rejects).
type StorageMode = "shared" | "dedicated";

interface StorageModeOption {
  value: StorageMode;
  label: string;
  description: string;
  perks: readonly string[];
}

const STORAGE_MODES: readonly StorageModeOption[] = [
  {
    value: "shared",
    label: "Shared (pool)",
    description: "Default — multi-tenant pool with kernel-enforced subPath isolation",
    perks: [
      "~R$0/mo marginal storage",
      "subPath-only isolation (Kubernetes pattern)",
      "Best for internal / test / demo orgs",
    ],
  },
  {
    value: "dedicated",
    label: "Dedicated share",
    description: "Per-org Premium NFS share with per-share ACL",
    perks: [
      "~R$130/mo storage floor",
      "Per-share ACL isolation (compliance story)",
      "Best for paying-customer orgs",
    ],
  },
];

// Simple, ASCII-only slug — same heuristic as the legacy CreateOrgDialog.
function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

// ── Wizard ───────────────────────────────────────────────────────

export interface CreateOrgWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called once provisioning of both the org AND the first admin user
   *  reach `completed`. The Organizations page reloads its list on this. */
  onProvisioned: (result: { orgId: string; userId: string; email: string }) => void;
}

type Stage =
  | { kind: "form"; step: 1 | 2 | 3 | 4 }
  | { kind: "provisioningOrg"; orgTaskId: string }
  | { kind: "provisioningUser"; orgId: string; userTaskId: string; email: string }
  | { kind: "done"; orgId: string; userId: string; email: string }
  | { kind: "error"; message: string; step: 1 | 2 | 3 | 4 };

const STEP_LABELS = ["Organization", "Plan", "First admin", "Review & provision"] as const;

/**
 * Describe why a provisioning pipeline failed, in the terms an operator can act
 * on: WHICH step died, and what it said.
 *
 * Written after a prod incident (2026-08-11, ARC Advogados) where this dialog
 * rendered only its generic fallback. The failing step (8 — health-check) and
 * the underlying error existed in the `taskRecords` document the whole time;
 * getting from the dialog to the cause took about an hour of querying prod
 * Mongo. Both facts were in hand at render time and were being discarded:
 *
 *  - `TaskRecord.steps[]` was never consulted, though the wizard renders those
 *    same step names live while provisioning runs.
 *  - `TaskRecord.error` is `string | Record<string, unknown>`, and the old
 *    `typeof … === "string" ? … : <generic>` ternary dropped the object half
 *    entirely — so a STRUCTURED error rendered as "no detail", which reads as
 *    absence rather than as detail we threw away.
 *
 * Anything we genuinely do not have stays unrepresented rather than being
 * smoothed into a reassuring sentence: `fallback` is used only when the task
 * carries neither a failed step nor an error.
 */
export function describeTaskFailure(task: TaskRecord, fallback: string): string {
  const failed = task.steps?.find((s) => s.status === "failed");
  // `TaskStep.name` is TYPED `string` and can arrive ABSENT — the same "the type
  // is not the wire" trap this function already carries for `error`. lexserver
  // wrote user-provision records with a hand-retyped 8-name list against a
  // 10-step pipeline (fixed at 419152742, write-time only, NO backfill), so on
  // any record older than that fix the last two steps have no `name` at all and
  // the ones before are shifted. Interpolating it produced a literal
  // "Step 9/8 — undefined" on exactly the dialog the onboarding runbook §4.2
  // tells an operator to read. Name the step by NUMBER when the label is
  // missing rather than printing a word the record does not contain.
  const stepLabel = failed
    ? failed.name
      ? `Step ${failed.step}/${task.totalSteps} — ${failed.name}`
      : `Step ${failed.step}/${task.totalSteps} (step name not recorded)`
    : undefined;

  // Prefer the step's own error; fall back to the task-level one. BOTH go
  // through errorText: `TaskStep.error` is TYPED `string` and arrives as an
  // object, which is how the first cut of this shipped "[object Object]" to a
  // prod operator (2026-08-12). The type is not the wire.
  const detail = errorText(failed?.error) ?? errorText(task.error);

  if (stepLabel && detail) return `${stepLabel}: ${detail}`;
  if (stepLabel) return `${stepLabel} failed (no error text recorded on the step).`;
  if (detail) return detail;
  return fallback;
}

// v1.0 ships Brazil-only. The wizard shows these as readonly badges in
// Step 1 — when a non-BR firm onboards, this becomes a real dropdown
// step (and the Zod enums + this constant widen in tandem).
const LOCALIZATION_V1 = {
  locale: "pt-BR",
  jurisdiction: "BR",
  timezone: "America/Sao_Paulo",
  currency: "BRL",
} as const;

// ── Phase 5 Slice 5.3 — credentials banner ──────────────────────
//
// Hoisted to module scope so both the helper (`CredentialsBanner`) and
// the wizard's `credStatus` state reference the same named type — no
// inline-shape duplication.
type CredStatus = {
  openaiOauth: "ok" | "missing" | "expired";
  openrouter: "ok" | "missing";
};
type CredStatusState =
  | { kind: "loading" }
  | { kind: "ok"; data: CredStatus }
  | { kind: "error"; message: string };

// Renders the credentials banner shown at step 4. NEVER blocks
// Provision — purely informational. See the `loadCredStatus` comment
// block inside `CreateOrgWizard` for the layering rationale.
function CredentialsBanner({
  state,
  isDev,
}: {
  state: CredStatusState;
  isDev: boolean;
}) {
  if (state.kind === "loading") {
    return (
      <div
        className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400"
        data-testid="wizard-cred-banner-loading"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        Verifying platform credentials…
      </div>
    );
  }
  if (state.kind === "error") {
    // Transient 5xx / 403 / unreachable — show a neutral hint instead
    // of a red error. The Phase 1 BullMQ assertion is the real gate;
    // the wizard's job here is observability, not enforcement.
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
        data-testid="wizard-cred-banner-unverified"
      >
        <Info className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
        <span>
          Couldn't verify platform credentials. Provisioning will still
          run — the pipeline asserts a usable seed and fails loud if missing.
        </span>
      </div>
    );
  }
  const { openaiOauth, openrouter } = state.data;
  // Local-dev hint: KV unset → both "missing" on a 200 response. Show
  // the dev-mode explanation rather than the prod amber-info copy.
  if (isDev && openaiOauth === "missing" && openrouter === "missing") {
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300"
        data-testid="wizard-cred-banner-dev"
      >
        <Info className="mt-0.5 h-3 w-3 shrink-0" />
        <span>
          Local dev — credentials sourced from <code className="font-mono">DEV_DEFAULT_*</code> env vars
          (no Azure Key Vault configured).
        </span>
      </div>
    );
  }
  const oauthOk = openaiOauth === "ok";
  const openrouterOk = openrouter === "ok";
  if (oauthOk && openrouterOk) {
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-green-300 bg-green-50 px-3 py-2 text-xs text-green-800 dark:border-green-700 dark:bg-green-950/30 dark:text-green-300"
        data-testid="wizard-cred-banner-ok"
      >
        <KeyRound className="mt-0.5 h-3 w-3 shrink-0" />
        <span>
          Platform OAuth + OpenRouter ✓ — new admin can use the OpenAI subscription out of the box.
        </span>
      </div>
    );
  }
  if (openaiOauth === "expired") {
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300"
        data-testid="wizard-cred-banner-expired"
      >
        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
        <span>
          Platform OAuth credential expired — central refresh will retry. Provisioning will use the
          Phase 1 platform-default API-key seed as a fallback.{" "}
          {/* `/platform/settings` is the Slice 5.4 page (next slice) — link
              pre-wired so the destination exists once 5.4 lands. */}
          <a href="/platform/settings" className="underline">
            Open Platform Settings
          </a>
        </span>
      </div>
    );
  }
  // Partial states — distinguish each so the copy is accurate (the
  // prior single fallback misreported "OAuth not staged" even when
  // OAuth WAS staged and only OpenRouter was missing).
  if (openaiOauth === "missing" && openrouter === "missing") {
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300"
        data-testid="wizard-cred-banner-missing"
      >
        <Info className="mt-0.5 h-3 w-3 shrink-0" />
        <span>
          Phase 5 OAuth + OpenRouter not staged. Provisioning will use the platform-default API
          key seed (Phase 1).{" "}
          <a href="/platform/settings" className="underline">
            Open Platform Settings
          </a>{" "}
          to stage them.
        </span>
      </div>
    );
  }
  if (openaiOauth === "missing") {
    // openrouter === "ok" — OpenRouter staged, OAuth not.
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300"
        data-testid="wizard-cred-banner-oauth-missing"
      >
        <Info className="mt-0.5 h-3 w-3 shrink-0" />
        <span>
          Phase 5 OAuth not staged (OpenRouter ✓). Provisioning will use the platform-default
          API key seed (Phase 1).{" "}
          <a href="/platform/settings" className="underline">
            Open Platform Settings
          </a>{" "}
          to stage the OAuth profile.
        </span>
      </div>
    );
  }
  // openaiOauth === "ok" && openrouter === "missing" — OAuth staged,
  // OpenRouter not. Provisioning succeeds via OAuth + the Phase 1
  // platform-default API key; orgs that want OpenRouter must add
  // their own at Slice 5.2's RuntimeConfigPage.
  return (
    <div
      className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
      data-testid="wizard-cred-banner-openrouter-missing"
    >
      <Info className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
      <span>
        Platform OAuth ✓ — OpenRouter not staged at the platform level (orgs can still configure
        their own at <strong>Org settings → Runtime config</strong>).{" "}
        <a href="/platform/settings" className="underline">
          Open Platform Settings
        </a>
      </span>
    </div>
  );
}

export function CreateOrgWizard({ open, onOpenChange, onProvisioned }: CreateOrgWizardProps) {
  const api = usePlatformApi();

  // ── Form state ────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [tier, setTier] = useState<Tier>("starter");
  // S5: storage allocation mode. "shared" is the wizard's user-visible
  // default (most new orgs land in the pool); operator picks "dedicated"
  // explicitly for paying-customer orgs that need per-share isolation.
  const [storageMode, setStorageMode] = useState<StorageMode>("shared");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");
  // §8.10 C3 — LLM credential source. "" = platform defaults (the seeded
  // path); an orgId = copy that org's encrypted credentials verbatim.
  const [copyCredsFromOrgId, setCopyCredsFromOrgId] = useState("");
  // ACTIVE orgs for the copy picker; null = still loading / load failed
  // (the picker degrades to platform-defaults-only, never blocks).
  const [copyOrgOptions, setCopyOrgOptions] = useState<
    Array<{ orgId: string; name: string; slug: string; createdAt: string }> | null
  >(null);

  // ── Wizard stage ──────────────────────────────────────────────
  const [stage, setStage] = useState<Stage>({ kind: "form", step: 1 });

  // ── Reset on close ────────────────────────────────────────────
  const reset = useCallback(() => {
    setName("");
    setSlug("");
    setTier("starter");
    setStorageMode("shared");
    setAdminEmail("");
    setAdminFirstName("");
    setAdminLastName("");
    setCopyCredsFromOrgId("");
    setStage({ kind: "form", step: 1 });
    // Clear cached credentials status so a reopen doesn't briefly flash
    // the previous session's banner before the mount fetch overwrites.
    setCredStatus({ kind: "loading" });
  }, []);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    // Defer the reset so the closing animation runs on the current state.
    setTimeout(reset, 200);
  }, [onOpenChange, reset]);

  // ── Step validation (controls Next button enabled-ness) ───────
  const step1Valid = name.trim().length >= 2 && /^[a-z0-9][a-z0-9-]*$/.test(slug);
  // Lightweight email regex: requires `local@domain.tld`. Server is the
  // authoritative validator — this is just to keep the Next button honest.
  const step3Valid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail.trim()) &&
    adminFirstName.trim().length > 0 &&
    adminLastName.trim().length > 0;

  const currentStep = stage.kind === "form" || stage.kind === "error" ? stage.step : 4;

  // Auto-derive slug from name unless the user has hand-edited it.
  const setNameAndSlug = (next: string) => {
    setName(next);
    if (slug === "" || slug === slugify(name)) {
      setSlug(slugify(next));
    }
  };

  // ── Phase 5 Slice 5.3 — platform-default credentials banner ───
  //
  // Calls the Phase 5 status probe (Slice 5.1's
  // `GET /api/platform/settings/credential-status`) so the operator
  // sees, on the review step, whether the Phase 5 OAuth + OpenRouter
  // KV secrets are staged. Informational only — the wizard NEVER
  // disables Provision based on this. The canonical mandatory-
  // credential gate is the Phase 1 `assertOrgHasUsableCredential`
  // BullMQ-pipeline check (`init-org-config.ts`), which fires loud
  // on miss → STEP_FAILED → clean rollback (surfaced live in the
  // `<ProvisioningProgress>` mounted below). In prod with only the
  // legacy `platform-default-{anthropic,openai}-key` KV staged,
  // this probe returns `{missing, missing}` and provisioning still
  // succeeds via the legacy API-key seed.
  const [credStatus, setCredStatus] = useState<CredStatusState>({ kind: "loading" });

  // `showLoading` controls whether to reset the banner to its
  // "Verifying…" state during the fetch. The mount load uses true
  // (we have no prior data); the step-4 refresh uses false so the
  // already-rendered banner doesn't flash back to a spinner when the
  // operator re-enters step 4 with data already cached.
  const loadCredStatus = useCallback(
    async (showLoading: boolean) => {
      if (showLoading) setCredStatus({ kind: "loading" });
      try {
        const res = await api.getPlatformCredentialStatus();
        setCredStatus({ kind: "ok", data: res.data });
      } catch (err) {
        setCredStatus({
          kind: "error",
          message: err instanceof Error ? err.message : "verify-failed",
        });
      }
    },
    [api],
  );

  // Fetch on mount AND on transition INTO step 4. The second fetch
  // matters when an operator opens the wizard, walks to step 4, then
  // stages KV in another tab — without a refresh the banner stays
  // stale until close+reopen. Cheap call (Slice 5.1 KV probe).
  useEffect(() => {
    if (!open) return;
    void loadCredStatus(true);
  }, [open, loadCredStatus]);

  // §8.10 C3 — load the ACTIVE orgs for the copy-credentials picker once per
  // open. Newest-`createdAt` first so the D-1 default (copy-provider-settings
  // plan §4.5) can preselect the last-provisioned org. Failure degrades to
  // platform-defaults-only (never blocks).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.listOrgs({ status: "ACTIVE", limit: 100 });
        if (cancelled) return;
        const opts = res.data
          .map((o) => ({ orgId: o.orgId, name: o.name, slug: o.slug, createdAt: o.createdAt }))
          .sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));
        setCopyOrgOptions(opts);
        // D-1 — default the source to the LAST-PROVISIONED org (the common
        // "make the next org like the last" case); the operator can switch to
        // "Platform defaults" or another org. Bootstrap: no prior org ⇒ stays
        // on platform defaults. Only sets the initial value (empty state).
        setCopyCredsFromOrgId((prev) => (prev === "" && opts.length > 0 ? opts[0]!.orgId : prev));
      } catch {
        if (!cancelled) setCopyOrgOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, api]);
  // Derived boolean so the effect only fires on the actual
  // transition into step 4 (not on every stage-object identity change).
  const isOnStep4 = stage.kind === "form" && stage.step === 4;
  useEffect(() => {
    if (isOnStep4) void loadCredStatus(false);
  }, [isOnStep4, loadCredStatus]);

  // ── Step 4 — kick off provisioning ────────────────────────────
  const handleProvision = useCallback(async () => {
    try {
      const orgRes = await api.createOrg({
        name: name.trim(),
        slug: slug.trim(),
        tier,
        // S5: storage allocation mode. Sent explicitly so the wizard's
        // "shared" default lands instead of the server's "dedicated"
        // back-compat default. The choice is captured in the audit log.
        storageMode,
        // Send the localization explicitly so the audit log captures the
        // choice — even though v1.0 effectively pins these to the BR
        // defaults. Schema defaults on the server still cover any wizard
        // build that omits the fields.
        locale: LOCALIZATION_V1.locale,
        jurisdiction: LOCALIZATION_V1.jurisdiction,
        timezone: LOCALIZATION_V1.timezone,
        currency: LOCALIZATION_V1.currency,
        // §8.10 C3 — omit entirely when "Platform defaults" is selected.
        copyLlmCredentialsFromOrgId: copyCredsFromOrgId || undefined,
      });
      setStage({ kind: "provisioningOrg", orgTaskId: orgRes.data.taskId });
    } catch (err) {
      setStage({
        kind: "error",
        step: 4,
        message: err instanceof Error ? err.message : "Failed to start org provisioning",
      });
    }
  }, [api, name, slug, tier, storageMode, copyCredsFromOrgId]);

  // ── org-provision terminal → fire user-provision ──────────────
  const handleOrgTerminal = useCallback(async (event: { ok: boolean; task: TaskRecord }) => {
    if (!event.ok) {
      setStage({
        kind: "error",
        step: 4,
        message: describeTaskFailure(
          event.task,
          "Org provisioning failed — see pipeline detail and retry from the Organizations list",
        ),
      });
      return;
    }
    const orgId = event.task.orgId;
    try {
      const userRes = await api.createOrgUser(orgId, {
        email: adminEmail.trim(),
        firstName: adminFirstName.trim(),
        lastName: adminLastName.trim(),
        role: "ORG_ADMIN",
      });
      setStage({
        kind: "provisioningUser",
        orgId,
        userTaskId: userRes.data.taskId,
        email: adminEmail.trim(),
      });
    } catch (err) {
      setStage({
        kind: "error",
        step: 4,
        message: err instanceof Error ? err.message : "Failed to start user provisioning",
      });
    }
  }, [api, adminEmail, adminFirstName, adminLastName]);

  const handleUserTerminal = useCallback((event: { ok: boolean; task: TaskRecord }) => {
    if (stage.kind !== "provisioningUser") return;
    if (!event.ok) {
      setStage({
        kind: "error",
        step: 4,
        // The old copy ended "Open the Users page to retry." — which invites a
        // retry that fails identically when the cause is structural (ARC's was:
        // the runtime could not write its storage volume). Naming the step lets
        // the operator judge whether a retry is even plausible; the fallback no
        // longer promises that it is.
        message: describeTaskFailure(
          event.task,
          "User provisioning failed — the org was created but the first admin was not. Check the task's pipeline detail before retrying from the Users page.",
        ),
      });
      return;
    }
    setStage({ kind: "done", orgId: stage.orgId, userId: event.task.userId ?? "", email: stage.email });
    onProvisioned({ orgId: stage.orgId, userId: event.task.userId ?? "", email: stage.email });
  }, [stage, onProvisioned]);

  // ── Render ────────────────────────────────────────────────────
  const stepDots = useMemo(() => (
    <div className="flex items-center justify-center gap-1.5" data-testid="wizard-step-dots">
      {STEP_LABELS.map((label, i) => {
        const idx = (i + 1) as 1 | 2 | 3 | 4;
        const reached = currentStep >= idx;
        return (
          <div
            key={label}
            className={
              "flex items-center gap-1 text-[11px] " +
              (reached ? "text-indigo-600 dark:text-indigo-400" : "text-slate-400 dark:text-slate-600")
            }
            data-testid={`wizard-dot-${idx}`}
            data-reached={reached}
          >
            <span
              className={
                "flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold " +
                (reached
                  ? "bg-indigo-600 text-white"
                  : "bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400")
              }
            >
              {idx}
            </span>
            <span className="hidden sm:inline">{label}</span>
          </div>
        );
      })}
    </div>
  ), [currentStep]);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) { handleClose(); } else { onOpenChange(true); } }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-500" />
            New Organization
          </DialogTitle>
          <DialogDescription>
            Provision an organization plus its first administrator. Both pipelines run asynchronously.
          </DialogDescription>
        </DialogHeader>

        <div className="my-2">{stepDots}</div>

        {/* ── Step content ───────────────────────────────────── */}

        {stage.kind === "form" && stage.step === 1 && (
          <div className="space-y-4" data-testid="wizard-step-1">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600 dark:text-slate-400">
                Organization name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setNameAndSlug(e.target.value)}
                placeholder="Lexiun Demo Co"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400 dark:border-slate-700 dark:bg-slate-900"
                autoFocus
                data-testid="wizard-input-name"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600 dark:text-slate-400">
                Slug
                <span className="ml-1 text-slate-400">(auto-derived; lowercase + dashes)</span>
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                pattern="^[a-z0-9][a-z0-9-]*$"
                placeholder="lexiun-demo-co"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-indigo-400 dark:border-slate-700 dark:bg-slate-900"
                data-testid="wizard-input-slug"
              />
            </div>
            <div className="space-y-1.5 rounded-md border border-dashed border-slate-200 p-3 dark:border-slate-700">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-400">
                <Globe className="h-3.5 w-3.5 text-indigo-500" />
                Localization
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="wizard-localization-badges">
                <Badge label="Brazil" value={LOCALIZATION_V1.jurisdiction} testId="wizard-loc-jurisdiction" />
                <Badge label="Portuguese" value={LOCALIZATION_V1.locale} testId="wizard-loc-locale" />
                <Badge label="São Paulo" value={LOCALIZATION_V1.timezone} testId="wizard-loc-timezone" />
                <Badge label="Brazilian Real" value={LOCALIZATION_V1.currency} testId="wizard-loc-currency" />
              </div>
              <p className="text-[10px] text-slate-400">
                v1.0 ships Brazil-only — multi-jurisdiction support arrives in v1.1.
              </p>
            </div>
          </div>
        )}

        {stage.kind === "form" && stage.step === 2 && (
          <div className="space-y-4" data-testid="wizard-step-2">
            <p className="text-xs text-slate-500">Pick a subscription tier. Resource quotas + user caps follow `hosting-deployment.md` §6.2.</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {TIERS.map((opt) => {
                const selected = tier === opt.value;
                return (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => setTier(opt.value)}
                    aria-pressed={selected}
                    data-testid={`wizard-tier-${opt.value}`}
                    className={
                      "rounded-lg border p-3 text-left transition-colors " +
                      (selected
                        ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30"
                        : "border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800")
                    }
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Sparkles className={"h-3.5 w-3.5 " + (selected ? "text-indigo-600" : "text-slate-400")} />
                        <span className="text-sm font-semibold">{opt.label}</span>
                      </div>
                    </div>
                    <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{opt.description}</p>
                    <ul className="mt-2 space-y-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                      {opt.perks.map((p) => (
                        <li key={p} className="flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3 text-green-500" /> {p}
                        </li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>

            {/* S5 — Storage allocation mode (CREATE-only; immutable post-provision). */}
            <div className="space-y-2 pt-2">
              <p className="text-xs text-slate-500">
                Storage mode. <strong>Shared</strong> (default) is the lowest-cost option — orgs share one Premium NFS pool with kernel-enforced subPath isolation. <strong>Dedicated</strong> gives each org its own ~R$130/mo Premium NFS share for the cleaner per-share-ACL compliance story. See <code>ai-docs/lexiun-azure-storage.md</code>.
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="wizard-storage-modes">
                {STORAGE_MODES.map((opt) => {
                  const selected = storageMode === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => setStorageMode(opt.value)}
                      aria-pressed={selected}
                      data-testid={`wizard-storage-mode-${opt.value}`}
                      className={
                        "rounded-lg border p-3 text-left transition-colors " +
                        (selected
                          ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30"
                          : "border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800")
                      }
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <ShieldCheck className={"h-3.5 w-3.5 " + (selected ? "text-indigo-600" : "text-slate-400")} />
                          <span className="text-sm font-semibold">{opt.label}</span>
                        </div>
                      </div>
                      <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{opt.description}</p>
                      <ul className="mt-2 space-y-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                        {opt.perks.map((p) => (
                          <li key={p} className="flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3 text-green-500" /> {p}
                          </li>
                        ))}
                      </ul>
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400">
                Storage mode is fixed at provision time — there is no in-place switch. Migrating an existing org between modes requires data copy + container respawn (manual).
              </p>
            </div>

            {/* §8.10 C3 — LLM credential source (platform defaults vs copy-from-org). */}
            <div className="space-y-1.5 rounded-md border border-dashed border-slate-200 p-3 dark:border-slate-700" data-testid="wizard-copy-creds-block">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-400">
                <KeyRound className="h-3.5 w-3.5 text-indigo-500" />
                AI provider source
              </div>
              <select
                value={copyCredsFromOrgId}
                onChange={(e) => setCopyCredsFromOrgId(e.target.value)}
                aria-label="AI provider source"
                data-testid="wizard-copy-creds-select"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400 dark:border-slate-700 dark:bg-slate-900"
              >
                <option value="">Platform defaults (seeded at provision)</option>
                {(copyOrgOptions ?? []).map((o, i) => (
                  <option key={o.orgId} value={o.orgId}>
                    Copy from: {o.name} ({o.slug}){i === 0 ? " — most recent" : ""}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-slate-400">
                {copyCredsFromOrgId
                  ? "The source org's encrypted credentials (API keys, OAuth, OpenRouter, dynamic table) are copied verbatim and REPLACE the platform seed. Model aliases still derive from the platform catalog. (Defaults to the most recently provisioned org; use the Copy AI Settings dialog after creation to fine-tune which categories.)"
                  : copyOrgOptions === null
                    ? "Loading existing orgs…"
                    : "New org inherits the platform seed credentials. Or pick an org above to copy its credential setup verbatim instead."}
              </p>
            </div>
          </div>
        )}

        {stage.kind === "form" && stage.step === 3 && (
          <div className="space-y-4" data-testid="wizard-step-3">
            <p className="text-xs text-slate-500">
              An invitation email is sent to this address by Clerk. The new admin clicks the link in
              the email, sets their password, and signs in to LexIDE normally.
            </p>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Email</label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                placeholder="admin@firm.com"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400 dark:border-slate-700 dark:bg-slate-900"
                autoFocus
                data-testid="wizard-input-email"
              />
              {import.meta.env.DEV && (
                <p className="text-[10px] text-slate-400">
                  Tip — in dev, use a <code>+clerk_test</code> subaddress (e.g.
                  <code>admin+clerk_test@example.com</code>). Invitation emails to these
                  addresses don't go through real email delivery; copy the URL from the Clerk
                  dashboard's Invitations tab.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400">First name</label>
                <input
                  type="text"
                  value={adminFirstName}
                  onChange={(e) => setAdminFirstName(e.target.value)}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400 dark:border-slate-700 dark:bg-slate-900"
                  data-testid="wizard-input-firstName"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Last name</label>
                <input
                  type="text"
                  value={adminLastName}
                  onChange={(e) => setAdminLastName(e.target.value)}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400 dark:border-slate-700 dark:bg-slate-900"
                  data-testid="wizard-input-lastName"
                />
              </div>
            </div>
            <div className="text-[11px] text-slate-500">
              Role is locked to <strong>ORG_ADMIN</strong> — additional users (and member roles) are added
              later from the org-admin Users page.
            </div>
          </div>
        )}

        {stage.kind === "form" && stage.step === 4 && (
          <div className="space-y-3" data-testid="wizard-step-4">
            <p className="text-xs text-slate-500">
              Review the values below. On <strong>Provision</strong>, the org pipeline runs first; once the
              org reaches <code>ACTIVE</code>, the user pipeline starts automatically.
            </p>
            <CredentialsBanner state={credStatus} isDev={import.meta.env.DEV} />
            <Card>
              <CardContent className="space-y-2 pt-4 text-sm">
                <Row label="Organization">{name}</Row>
                <Row label="Slug" mono>{slug}</Row>
                <Row label="Tier">{TIERS.find((t) => t.value === tier)?.label}</Row>
                <Row label="Storage">
                  <span data-testid="wizard-review-storage-mode">
                    {STORAGE_MODES.find((s) => s.value === storageMode)?.label}
                  </span>
                </Row>
                <Row label="Localization" mono>
                  {LOCALIZATION_V1.locale} · {LOCALIZATION_V1.jurisdiction} · {LOCALIZATION_V1.timezone} · {LOCALIZATION_V1.currency}
                </Row>
                <Row label="Credentials">
                  <span data-testid="wizard-review-copy-creds">
                    {copyCredsFromOrgId
                      ? `Copy from ${
                          copyOrgOptions?.find((o) => o.orgId === copyCredsFromOrgId)?.name ??
                          copyCredsFromOrgId
                        }`
                      : "Platform defaults"}
                  </span>
                </Row>
                <Row label="First admin">{adminFirstName} {adminLastName} &lt;{adminEmail}&gt;</Row>
                <Row label="Role">ORG_ADMIN</Row>
              </CardContent>
            </Card>
          </div>
        )}

        {stage.kind === "provisioningOrg" && (
          <div className="space-y-4" data-testid="wizard-stage-org">
            <ProvisioningProgress
              taskId={stage.orgTaskId}
              title="Org pipeline"
              onTerminal={handleOrgTerminal}
            />
          </div>
        )}

        {stage.kind === "provisioningUser" && (
          <div className="space-y-4" data-testid="wizard-stage-user">
            <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" /> Org provisioned
            </div>
            <ProvisioningProgress
              taskId={stage.userTaskId}
              title="User pipeline"
              onTerminal={handleUserTerminal}
            />
          </div>
        )}

        {stage.kind === "done" && (
          <div className="space-y-3" data-testid="wizard-stage-done">
            <div className="rounded-lg border border-green-300 bg-green-50 p-4 text-sm dark:border-green-700 dark:bg-green-950/30">
              <div className="flex items-center gap-2 font-medium text-green-700 dark:text-green-300">
                <CheckCircle2 className="h-4 w-4" /> Org + invitation sent
              </div>
              <p className="mt-2 text-xs text-green-700/90 dark:text-green-300/80">
                A Clerk invitation has been sent to <strong>{stage.email}</strong>. They'll click the
                link in the email to set their password, then sign in to LexIDE normally.
                {import.meta.env.DEV && (
                  <>
                    {" "}In dev, real email isn't delivered to <code>+clerk_test</code> addresses —
                    grab the invitation URL from the Clerk dashboard's Invitations tab.
                  </>
                )}
              </p>
              <p className="mt-2 text-xs text-green-700/80 dark:text-green-300/70">
                Once signed in, your admin can connect their own OpenAI OAuth (or paste an OpenRouter key)
                at <strong>Org settings → Runtime config</strong>.
              </p>
              {import.meta.env.DEV && (
                <p className="mt-2 text-xs text-green-700/80 dark:text-green-300/70 inline-flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {/*
                    Phase 5 Slice 5.3: dev link now lands directly on
                    LexIDE's `RuntimeConfigPage` (Slice 5.2) so the new
                    admin sees the OAuth-connect UI immediately after
                    accepting their Clerk invitation + signing in.
                    `data-testid="wizard-lexide-link"` preserved —
                    `seed-fixtures.spec.ts:282` reads the href into the
                    audit trail with no content assertion.
                  */}
                  <a
                    href="http://localhost:5175/org/config"
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                    data-testid="wizard-lexide-link"
                  >
                    Open LexIDE → Runtime config (dev)
                  </a>
                </p>
              )}
            </div>
          </div>
        )}

        {stage.kind === "error" && (
          <div
            data-testid="wizard-error"
            className="rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-700 dark:border-red-700 dark:bg-red-950/30 dark:text-red-300"
          >
            <div className="flex items-center gap-1.5 font-medium">
              <AlertTriangle className="h-3.5 w-3.5" />
              Provisioning failed
            </div>
            <p className="mt-1" data-testid="wizard-error-message">
              {stage.message}
            </p>
          </div>
        )}

        {/* ── Footer / nav ───────────────────────────────────── */}

        <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3 dark:border-slate-700">
          <div className="text-[11px] text-slate-400">
            <ShieldCheck className="mr-1 inline h-3 w-3" />
            PLATFORM_ADMIN only.
          </div>
          <div className="flex items-center gap-2">
            {stage.kind === "form" && stage.step > 1 && (
              <button
                type="button"
                onClick={() => setStage({ kind: "form", step: (stage.step - 1) as 1 | 2 | 3 })}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                data-testid="wizard-back"
              >
                <ArrowLeft className="h-3 w-3" /> Back
              </button>
            )}
            {stage.kind === "form" && stage.step < 4 && (
              <button
                type="button"
                onClick={() => setStage({ kind: "form", step: (stage.step + 1) as 2 | 3 | 4 })}
                disabled={
                  (stage.step === 1 && !step1Valid) ||
                  (stage.step === 3 && !step3Valid)
                }
                className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="wizard-next"
              >
                Next <ArrowRight className="h-3 w-3" />
              </button>
            )}
            {stage.kind === "form" && stage.step === 4 && (
              <button
                type="button"
                onClick={handleProvision}
                className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                data-testid="wizard-provision"
              >
                Provision <ArrowRight className="h-3 w-3" />
              </button>
            )}
            {(stage.kind === "provisioningOrg" || stage.kind === "provisioningUser") && (
              <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                <Loader2 className="h-3 w-3 animate-spin" /> Provisioning…
              </span>
            )}
            {(stage.kind === "done" || stage.kind === "error") && (
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex items-center gap-1 rounded-md bg-slate-700 px-4 py-1.5 text-xs font-medium text-white hover:bg-slate-800 dark:bg-slate-300 dark:text-slate-900 dark:hover:bg-slate-200"
                data-testid="wizard-close"
              >
                Close <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Internal helper ──────────────────────────────────────────────

function Row({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="w-24 shrink-0 text-slate-500 dark:text-slate-400">{label}</span>
      <span className={mono ? "font-mono text-slate-700 dark:text-slate-200" : "text-slate-700 dark:text-slate-200"}>
        {children}
      </span>
    </div>
  );
}

function Badge({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
    >
      <span className="font-medium">{label}</span>
      <span className="font-mono text-slate-400 dark:text-slate-500">{value}</span>
    </span>
  );
}
